import { lazy } from 'react';
import type { ResolveRoute, RouterContext } from 'universal-router';
import UniversalRouter from 'universal-router';

import type { ResolvedRoute } from '@routes/types';
import { hasFeatureEntitlement } from '@components/entitlements/entitlements';
import { hasFeatureFlag } from '@components/feature-flag/feature-flag';

import routes from './routes';

const extendRouteContext: ResolveRoute<ResolvedRoute> = async ({
  pathname,
  baseUrl,
  query = {},
  params,
  ...context
}) => {
  const { parent, load, path } = context.route;
  const route = !parent?.path ? context.route : parent;
  const { navbar, redirect } = route;

  const description = route.description ?? '';

  const title = navbar?.title ?? 'Untitled Page';

  const routeMask = baseUrl + path;
  const { hash } = context;

  return {
    baseUrl,
    description,
    params,
    pathname,
    query,
    redirect,
    route,
    routeMask,
    hash,
    title,
    chunks: [route.chunkName ?? 'not-found'].filter(Boolean),
    Component: lazy(load!),
  } as ResolvedRoute;
};

export const createRouter = (initialContext?: RouterContext) =>
  new UniversalRouter<ResolvedRoute>(routes, {
    context: initialContext,
    resolveRoute: async (context, params) => {
      const { entitlements = {}, featureFlags = [], route } = context;

      if (
        !hasFeatureEntitlement(entitlements, route.entitlement) ||
        !hasFeatureFlag(featureFlags, route.featureFlag)
      ) {
        return context.next(false);
      }

      if (typeof route.load === 'function') {
        return extendRouteContext(context, params);
      }

      return undefined;
    },
  });

export const router = createRouter({
  featureFlags: [],
  entitlements: {},
});

export const updateRouterContext = (newContext: Partial<RouterContext>) => {
  router.options.context = {
    ...router.options.context!,
    ...newContext,
  };
};

export default router;
